import { Box, Modal, ModalDialog } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import bbox from '@turf/bbox';
import { useMap } from '@vis.gl/react-maplibre';

import { BackendTable } from '@/components/backend/table/BackendTable';
import { ModalCardHeader } from '@/components/modals/ModalCardHeader';
import type { AdminLevel } from '@/modules/api';
import { AdminLevelType } from '@/modules/api';
import { backendApiAdminLevelsFindAllOptions } from '@/modules/api/@tanstack/react-query.gen';
import { requestFeature } from '@/queries/useFeaturesQuery';
import type { AdminLevelFeature } from '@/types/AdminLevelFeature';

export const Route = createFileRoute('/_public/teilnehmende-kommunen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

const columnHelper = createColumnHelper<AdminLevel>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    enableColumnFilter: false,
  }),
] as ColumnDef<AdminLevel>[];

function RouteComponent() {
  const navigate = useNavigate();

  const { data, isFetching } = useQuery(
    backendApiAdminLevelsFindAllOptions({
      query: {
        type: [AdminLevelType.MUNICIPALITY, AdminLevelType.CITY_COUNTY],
        active: true,
      },
    }),
  );

  const map = useMap();

  return (
    <Modal
      open
      onClose={() => {
        navigate({ to: '/' });
      }}
    >
      <ModalDialog maxWidth="md" minWidth="md">
        <ModalCardHeader title="Teilnehmende Kommunen" />

        <Box sx={{ overflow: 'auto' }}>
          <BackendTable
            columns={columns}
            data={data}
            getRowId={(row) => row.id}
            isFetching={isFetching}
            searchInputPlaceholder="Suche nach Name"
            subject="Verwaltungsebenen"
            onClickNavigate={async (row) => {
              const adminLevelFeature = await requestFeature<AdminLevelFeature>({
                collection: 'admin_level_view',
                query: {
                  filter: `id = '${row.id}'`,
                  precision: '6',
                },
              });
              const featBbox = bbox(adminLevelFeature);

              map.current?.fitBounds(featBbox as [number, number, number, number], { padding: 16 });

              return '/';
            }}
          />
        </Box>
      </ModalDialog>
    </Modal>
  );
}
